/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wallet, Check, X, Eye, Clock,
  DollarSign, AlertTriangle, CheckCircle, XCircle, TrendingUp, RefreshCw,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { useWithdrawals, useApproveWithdrawal, useRejectWithdrawal, useMarkWithdrawalPaid } from '@/hooks/api/useWithdrawals';
import { WithdrawalStatus, Withdrawal } from '@/types/superadmin.types';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { WithdrawalDetailModal } from '@/components/superadmin/WithdrawalDetailModal';

// Dialog state type
interface DialogState {
  isOpen: boolean;
  withdrawal: Withdrawal | null;
}

// Status Badge
const StatusBadge: React.FC<{ status: WithdrawalStatus; t: (key: string) => string }> = ({ status, t }) => {
  const config = {
    PENDING: { bg: 'bg-amber-500/15', text: 'text-amber-500', icon: Clock },
    APPROVED: { bg: 'bg-blue-500/15', text: 'text-blue-500', icon: CheckCircle },
    PAID: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle },
    REJECTED: { bg: 'bg-red-500/15', text: 'text-red-500', icon: XCircle },
  };
  const { bg, text, icon: Icon } = config[status];

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {t(`withdrawals.status.${status.toLowerCase()}`)}
    </span>
  );
};

// Skeleton Row Component
const SkeletonRow: React.FC = () => (
  <div className="withdrawals-table-row grid grid-cols-[1.5fr_100px_1fr_1fr_120px_100px_120px] gap-4 px-4 py-3 items-center border-b border-border-subtle animate-pulse">
    <div className="h-4 w-32 bg-muted/20 rounded"></div>
    <div className="h-5 w-16 bg-muted/20 rounded col-amount"></div>
    <div className="h-4 w-24 bg-muted/20 rounded col-method"></div>
    <div className="h-4 w-28 bg-muted/20 rounded col-details"></div>
    <div className="h-4 w-20 bg-muted/20 rounded col-requested"></div>
    <div className="h-5 w-16 bg-muted/20 rounded-full"></div>
    <div className="flex gap-1">
      <div className="w-7 h-7 bg-muted/20 rounded"></div>
      <div className="w-7 h-7 bg-muted/20 rounded"></div>
    </div>
  </div>
);

// Stat Card Skeleton
const StatCardSkeleton: React.FC = () => (
  <div className="bg-surface border border-border-subtle rounded-xl p-4 animate-pulse">
    <div className="w-10 h-10 rounded-lg bg-muted/20 mb-2"></div>
    <div className="h-6 w-16 bg-muted/20 rounded mb-1"></div>
    <div className="h-3 w-24 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  color: 'amber' | 'green' | 'blue' | 'red';
}> = ({ icon, value, label, color }) => {
  const colors = {
    amber: 'bg-amber-500/15 text-amber-500',
    green: 'bg-emerald-500/15 text-emerald-500',
    blue: 'bg-blue-500/15 text-blue-500',
    red: 'bg-red-500/15 text-red-500',
  };

  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${colors[color]}`}>
        {icon}
      </div>
      <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
};

// Main Withdrawals Page
export default function WithdrawalsPage() {
  const { t } = useTranslation('platform');
  const [statusFilter, setStatusFilter] = useState<WithdrawalStatus | 'ALL'>('PENDING');
  const [currentPage, setCurrentPage] = useState(0);

  // Dialog states
  const [approveDialog, setApproveDialog] = useState<DialogState>({ isOpen: false, withdrawal: null });
  const [rejectDialog, setRejectDialog] = useState<DialogState>({ isOpen: false, withdrawal: null });
  const [markPaidDialog, setMarkPaidDialog] = useState<DialogState>({ isOpen: false, withdrawal: null });

  // Detail modal state
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Use the withdrawals hook with filters
  const { data, isLoading, isFetching, refetch } = useWithdrawals({
    page: currentPage,
    size: 20,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
  });

  // Mutations
  const approveMutation = useApproveWithdrawal();
  const rejectMutation = useRejectWithdrawal();
  const markPaidMutation = useMarkWithdrawalPaid();

  const withdrawals = data?.content || [];
  const totalElements = data?.page?.totalElements || 0;
  const totalPages = data?.page?.totalPages || 1;

  // Calculate stats from current data
  const stats = useMemo(() => {
    const pendingCount = withdrawals.filter(w => w.status === 'PENDING').length;
    const pendingTotal = withdrawals.filter(w => w.status === 'PENDING').reduce((sum, w) => sum + w.amount, 0);
    const paidTotal = withdrawals.filter(w => w.status === 'PAID').reduce((sum, w) => sum + w.amount, 0);
    return { pendingCount, pendingTotal, paidTotal, total: withdrawals.length };
  }, [withdrawals]);

  // Open approve dialog
  const openApproveDialog = (withdrawal: Withdrawal) => {
    setApproveDialog({ isOpen: true, withdrawal });
  };

  // Open reject dialog
  const openRejectDialog = (withdrawal: Withdrawal) => {
    setRejectDialog({ isOpen: true, withdrawal });
  };

  // Open mark paid dialog
  const openMarkPaidDialog = (withdrawal: Withdrawal) => {
    setMarkPaidDialog({ isOpen: true, withdrawal });
  };

  // Handle approve confirmation
  const handleApproveConfirm = () => {
    if (!approveDialog.withdrawal) return;
    approveMutation.mutate({ id: approveDialog.withdrawal.id }, {
      onSuccess: () => {
        setApproveDialog({ isOpen: false, withdrawal: null });
        refetch();
      },
      onError: () => {
        setApproveDialog({ isOpen: false, withdrawal: null });
      },
    });
  };

  // Handle reject confirmation
  const handleRejectConfirm = (reason?: string) => {
    if (!rejectDialog.withdrawal || !reason) return;
    rejectMutation.mutate({ id: rejectDialog.withdrawal.id, data: { notes: reason } }, {
      onSuccess: () => {
        setRejectDialog({ isOpen: false, withdrawal: null });
        refetch();
      },
      onError: () => {
        setRejectDialog({ isOpen: false, withdrawal: null });
      },
    });
  };

  // Handle mark paid confirmation
  const handleMarkPaidConfirm = () => {
    if (!markPaidDialog.withdrawal) return;
    markPaidMutation.mutate({ id: markPaidDialog.withdrawal.id, data: {} }, {
      onSuccess: () => {
        setMarkPaidDialog({ isOpen: false, withdrawal: null });
        refetch();
      },
      onError: () => {
        setMarkPaidDialog({ isOpen: false, withdrawal: null });
      },
    });
  };

  // Handle view withdrawal detail
  const handleViewDetail = (withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedWithdrawal(null);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Wallet className="w-6 h-6" /> {t('withdrawals.title')}
          </h1>
          <p className="text-sm text-muted">{t('withdrawals.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-border hover:bg-hover disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          {!isLoading && stats.pendingCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/15 text-amber-500 rounded-lg">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">{t('withdrawals.pendingAlert', { count: stats.pendingCount, amount: stats.pendingTotal.toLocaleString() })}</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              value={stats.pendingCount.toString()}
              label={t('withdrawals.stats.pendingRequests')}
              color="amber"
            />
            <StatCard
              icon={<DollarSign className="w-5 h-5" />}
              value={`$${stats.pendingTotal.toLocaleString()}`}
              label={t('withdrawals.stats.pendingAmount')}
              color="amber"
            />
            <StatCard
              icon={<CheckCircle className="w-5 h-5" />}
              value={`$${stats.paidTotal.toLocaleString()}`}
              label={t('withdrawals.stats.paidThisMonth')}
              color="green"
            />
            <StatCard
              icon={<TrendingUp className="w-5 h-5" />}
              value={totalElements.toString()}
              label={t('withdrawals.stats.totalRequests')}
              color="blue"
            />
          </>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 p-1 bg-background rounded-lg w-fit">
        {(['PENDING', 'APPROVED', 'PAID', 'REJECTED', 'ALL'] as const).map((status) => (
          <button
            key={status}
            onClick={() => {
              setStatusFilter(status);
              setCurrentPage(0);
            }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              statusFilter === status
                ? 'bg-primary text-white'
                : 'text-muted hover:text-foreground hover:bg-surface'
            }`}
          >
            {t(`withdrawals.filters.${status.toLowerCase()}`)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <div className="withdrawals-table-header grid grid-cols-[1.5fr_100px_1fr_1fr_120px_100px_120px] gap-4 px-4 py-3 bg-background border-b border-border-subtle">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('withdrawals.table.partner')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-amount">{t('withdrawals.table.amount')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-method">{t('withdrawals.table.method')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-details">{t('withdrawals.table.details')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-requested">{t('withdrawals.table.requested')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('withdrawals.table.status')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('withdrawals.table.actions')}</div>
        </div>

        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : withdrawals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted">
            <Wallet className="w-12 h-12 mb-3 opacity-50" />
            <p>{t('withdrawals.empty')}</p>
          </div>
        ) : (
          withdrawals.map((withdrawal) => (
            <div
              key={withdrawal.id}
              className="withdrawals-table-row grid grid-cols-[1.5fr_100px_1fr_1fr_120px_100px_120px] gap-4 px-4 py-3 items-center border-b border-border-subtle last:border-b-0 hover:bg-hover"
            >
              <div className="font-medium text-foreground text-base">{withdrawal.partnerName}</div>
              <div className="font-mono text-base font-semibold text-foreground col-amount">${withdrawal.amount}</div>
              <div className="text-base text-secondary col-method">{withdrawal.paymentMethod}</div>
              <div className="text-sm text-muted font-mono truncate col-details">{withdrawal.paymentDetails}</div>
              <div className="text-sm text-muted col-requested">{formatDate(withdrawal.requestedAt)}</div>
              <div><StatusBadge status={withdrawal.status} t={t} /></div>
              <div className="flex gap-1">
                {withdrawal.status === 'PENDING' && (
                  <>
                    <button
                      onClick={() => openRejectDialog(withdrawal)}
                      disabled={rejectMutation.isPending}
                      className="p-1.5 rounded bg-red-500/15 hover:bg-red-500/25 transition-colors disabled:opacity-50"
                      title={t('withdrawals.actions.reject')}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                    <button
                      onClick={() => openApproveDialog(withdrawal)}
                      disabled={approveMutation.isPending}
                      className="p-1.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                      title={t('withdrawals.actions.approve')}
                    >
                      <Check className="w-4 h-4 text-emerald-500" />
                    </button>
                  </>
                )}
                {withdrawal.status === 'APPROVED' && (
                  <button
                    onClick={() => openMarkPaidDialog(withdrawal)}
                    disabled={markPaidMutation.isPending}
                    className="px-2 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {t('withdrawals.actions.markPaid')}
                  </button>
                )}
                {(withdrawal.status === 'PAID' || withdrawal.status === 'REJECTED') && (
                  <button
                    onClick={() => handleViewDetail(withdrawal)}
                    className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
                    title={t('withdrawals.actions.viewDetails')}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">
          {totalElements > 0 ? t('withdrawals.pagination.showing', {
            from: currentPage * 20 + 1,
            to: Math.min((currentPage + 1) * 20, totalElements),
            total: totalElements
          }) : t('withdrawals.pagination.noResults')}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted">
            {t('withdrawals.pagination.page', { current: currentPage + 1, total: Math.max(1, totalPages) })}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Approve Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={approveDialog.isOpen}
        variant="approve"
        title={t('withdrawals.dialogs.approve.title')}
        message={t('withdrawals.dialogs.approve.message')}
        details={approveDialog.withdrawal && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.partner')}:</span>
              <span className="font-medium">{approveDialog.withdrawal.partnerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.amount')}:</span>
              <span className="font-mono font-bold text-emerald-500">${approveDialog.withdrawal.amount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.method')}:</span>
              <span>{approveDialog.withdrawal.paymentMethod}</span>
            </div>
          </div>
        )}
        confirmText={t('withdrawals.actions.approve')}
        isLoading={approveMutation.isPending}
        onConfirm={handleApproveConfirm}
        onCancel={() => setApproveDialog({ isOpen: false, withdrawal: null })}
      />

      {/* Reject Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={rejectDialog.isOpen}
        variant="reject"
        title={t('withdrawals.dialogs.reject.title')}
        message={t('withdrawals.dialogs.reject.message')}
        details={rejectDialog.withdrawal && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.partner')}:</span>
              <span className="font-medium">{rejectDialog.withdrawal.partnerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.amount')}:</span>
              <span className="font-mono font-bold">${rejectDialog.withdrawal.amount}</span>
            </div>
          </div>
        )}
        showInput
        inputLabel={t('withdrawals.dialogs.reject.reasonLabel')}
        inputPlaceholder={t('withdrawals.dialogs.reject.reasonPlaceholder')}
        inputRequired
        confirmText={t('withdrawals.actions.reject')}
        isLoading={rejectMutation.isPending}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectDialog({ isOpen: false, withdrawal: null })}
      />

      {/* Mark as Paid Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={markPaidDialog.isOpen}
        variant="confirm"
        title={t('withdrawals.dialogs.markPaid.title')}
        message={t('withdrawals.dialogs.markPaid.message')}
        details={markPaidDialog.withdrawal && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.partner')}:</span>
              <span className="font-medium">{markPaidDialog.withdrawal.partnerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.amount')}:</span>
              <span className="font-mono font-bold text-emerald-500">${markPaidDialog.withdrawal.amount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('withdrawals.dialogs.labels.paymentDetails')}:</span>
              <span className="font-mono text-sm">{markPaidDialog.withdrawal.paymentDetails}</span>
            </div>
          </div>
        )}
        confirmText={t('withdrawals.actions.markPaid')}
        isLoading={markPaidMutation.isPending}
        onConfirm={handleMarkPaidConfirm}
        onCancel={() => setMarkPaidDialog({ isOpen: false, withdrawal: null })}
      />

      {/* Withdrawal Detail Modal */}
      <WithdrawalDetailModal
        withdrawal={selectedWithdrawal}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
      />

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .withdrawals-table-header,
          .withdrawals-table-row {
            grid-template-columns: 1fr 80px 80px 80px !important;
          }
          .col-method,
          .col-details,
          .col-requested {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          .withdrawals-table-header,
          .withdrawals-table-row {
            grid-template-columns: 1fr 60px 60px !important;
          }
          .col-amount {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
